import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldLabel } from "@/components/FieldLabel";
import ComboBox from "@/components/ComboBox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IsoPlacementPicker, { positionWords, splitPosition } from "@/components/settings/IsoPlacementPicker";
import CertificateNumbering from "@/components/settings/CertificateNumbering";
import DocumentControl from "@/components/settings/DocumentControl";
import { fmtDate, isoDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { FileText } from "lucide-react";
import { toast } from "sonner";

// What this page stores, in the order it shows them.
const SECTIONS = [
  ["org", "ISO reference"], ["documents", "document control"], ["numbering", "certificate numbering"],
  ["templates", "certificate template"], ["categories", "training categories"],
];
const ORG_FIELDS = ["iso_number", "iso_position_certificate", "iso_position_documents"];
const ORG_DRAFT = { iso_number: "", iso_position_certificate: "bottom-center", iso_position_documents: "bottom-center" };
const SLOT_TEXT = { left: "text-left", center: "text-center", right: "text-right" };

// A certificate's bottom line as the PDF lays it out (pdf_utils.draw_edge_row): the reference
// takes the slot chosen for it, and the number and the date keep their order in the others.
function bottomLine(items, note, align) {
  const free = note ? ["left", "center", "right"].filter((slot) => slot !== align) : ["left", "right"];
  const out = Object.fromEntries(free.map((slot, i) => [slot, items[i]]));
  if (note) out[align] = note;
  return out;
}

export default function Settings() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [templates, setTemplates] = useState([]);
  // With no template saved this form is what HR starts from, and the sample renders it. So it holds
  // the wording a certificate actually prints without a template (DEFAULT_CERTIFICATE_BODY in
  // pdf_utils.py) — the old "successfully participated in {training_title} on {training_date}" was
  // rejected wording that also repeated the title and date printed beneath it, and the first Save
  // would have put it on every certificate. test_wording.py holds the two together.
  const [t, setT] = useState({ name: "Default template", category: "", body_text: "has attended the training program named below.", signatory_1_name: "", signatory_1_designation: "", signatory_2_name: "", signatory_2_designation: "", is_default: false });
  // Two shapes of the same list: the box below edits it as one comma-separated string,
  // while the category picker needs it as items.
  const [categories, setCategories] = useState("");
  const [cats, setCats] = useState([]);
  const [hints, setHints] = useState({ designations: [] });
  // What the company says about itself on every document it prints, and where it says it.
  // `savedOrg` is what is stored, so the card can tell a change from what is already there.
  const [org, setOrg] = useState(ORG_DRAFT);
  const [savedOrg, setSavedOrg] = useState(ORG_DRAFT);
  const [savingOrg, setSavingOrg] = useState(false);
  // How certificate numbers are issued, as the server describes it: format, next number, restarts.
  const [numbering, setNumbering] = useState(null);
  // The header block of Shakun's controlled forms — Doc. No., revision, effective date.
  const [docs, setDocs] = useState(null);
  // Which of those actually arrived: absent while asking, false once it has failed. A
  // section that did not load is showing the draft this component starts with, and saving
  // it wrote that draft over what is stored — a blank ISO reference wiped the line from
  // every document printed afterwards, and a template with no id POSTed a second template.
  const [loaded, setLoaded] = useState({});

  // Settled one by one: under Promise.all a failed /suggestions, which only offers
  // designations, left every section on its draft with nothing said.
  //
  // Only the sections named in `keys` are asked for and put on screen. Try again used to
  // re-run all of them, and re-applying one that had already loaded threw away what was
  // being typed into it: retrying the ISO reference discarded an unsaved template.
  const load = useCallback(async (keys) => {
    const ask = (k, url) => (keys.includes(k) ? api.get(url) : Promise.resolve(null));
    const [tp, c, s, o, n, dc] = await Promise.allSettled([
      ask("templates", "/templates"), ask("categories", "/categories"), api.get("/suggestions"),
      ask("org", "/settings/organization"), ask("numbering", "/settings/certificate-numbering"),
      ask("documents", "/settings/documents")]);
    // "Answered" means the right shape, not just a 2xx: an HTML error page where JSON belongs
    // reached .find and .join below and blanked the screen instead of saying what failed.
    const isList = Array.isArray;
    const isRecord = (v) => !!v && typeof v === "object" && !Array.isArray(v);
    const isNumbering = (v) => isRecord(v) && typeof v.next_number === "string" && typeof v.series === "string";
    const isDocuments = (v) => isRecord(v) && typeof v.company_name === "string" && Array.isArray(v.documents);
    const ok = (r, shape) => r.status === "fulfilled" && r.value !== null && shape(r.value.data);
    if (ok(tp, isList)) {
      const list = tp.value.data;
      setTemplates(list);
      // The default, not the oldest. `list[0]` is whatever was created first, and the
      // certificate is issued from whichever is marked default — so with two templates,
      // Settings showed one while certificates printed the other, and nothing on the
      // screen said which was which. Same "two meanings, one surface" shape as
      // _get_default_template, which had it too.
      if (list.length) setT(list.find((x) => x.is_default) || list[0]);
    }
    if (ok(c, isList)) { setCategories(c.value.data.join(", ")); setCats(c.value.data); }
    if (ok(s, (v) => !!v && typeof v === "object")) setHints(s.value.data);
    if (ok(o, isRecord)) {
      const stored = { ...ORG_DRAFT, ...o.value.data };
      setOrg(stored);
      setSavedOrg(stored);
    }
    if (ok(n, isNumbering)) setNumbering(n.value.data);
    if (ok(dc, isDocuments)) setDocs(dc.value.data);
    // Merged, not replaced: a section that loaded stays loaded when a later ask fails, or
    // a blip on the reload after a template save locked a form holding what was just saved.
    const answers = { org: [o, isRecord], documents: [dc, isDocuments], numbering: [n, isNumbering],
                      templates: [tp, isList], categories: [c, isList] };
    setLoaded((cur) => ({ ...cur, ...Object.fromEntries(keys.map((k) => [k, !!cur[k] || ok(...answers[k])])) }));
  }, []);
  useEffect(() => { load(SECTIONS.map(([k]) => k)); }, [load]);
  const failed = SECTIONS.filter(([k]) => loaded[k] === false);
  const notLoaded = failed.map(([, name]) => name);
  // A Try again out right now. The banner says so and cannot be pressed twice: a second answer
  // would land over whatever had been typed into the section since the first.
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    try { await load(failed.map(([k]) => k)); } finally { setRetrying(false); }
  };

  const orgDirty = ORG_FIELDS.some((k) => org[k] !== savedOrg[k]);
  const saveOrg = async () => {
    if (!loaded.org) return;
    setSavingOrg(true);
    try {
      const r = await api.put("/settings/organization", Object.fromEntries(ORG_FIELDS.map((k) => [k, org[k]])));
      const stored = { ...ORG_DRAFT, ...r.data };
      setOrg(stored);
      setSavedOrg(stored);
      toast.success(stored.iso_number
        ? `ISO reference saved — ${positionWords(stored.iso_position_certificate)} on certificates, ${positionWords(stored.iso_position_documents)} on registers and sheets`
        : "ISO reference cleared");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingOrg(false);
  };

  const save = async () => {
    if (!loaded.templates) return;
    try {
      if (t.id) await api.put(`/templates/${t.id}`, t);
      else {
        // The id comes from this answer. Left to the reload below, a reload that failed would
        // leave the form without one, and the next Save would create a second template.
        const r = await api.post("/templates", t);
        if (r.data?.id) setT((cur) => ({ ...cur, id: r.data.id }));
      }
      toast.success("Template saved");
      load(["templates"]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCategories = async () => {
    if (!loaded.categories) return;
    try {
      await api.put("/categories", { categories: categories.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Categories updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // The real PDF, rendered by the server from what is on this screen before any of it is saved.
  // The HTML preview below is a sketch; this is the certificate's own renderer.
  const [sample, setSample] = useState(null); // { loading } | { url } | { error }
  const closeSample = () => {
    if (sample?.url) URL.revokeObjectURL(sample.url);
    setSample(null);
  };
  const openSample = async () => {
    setSample({ loading: true });
    try {
      const r = await api.post("/settings/certificate-preview.pdf", {
        iso_number: org.iso_number,
        iso_position_certificate: org.iso_position_certificate,
        ...(loaded.templates ? { template: t } : {}),
      }, { responseType: "blob" });
      if (!(r.data instanceof Blob) || r.data.type !== "application/pdf") throw new Error("not a PDF");
      const url = URL.createObjectURL(r.data);
      // Closed while it rendered: the answer is dropped rather than reopening the dialog.
      setSample((cur) => (cur ? { url } : (URL.revokeObjectURL(url), cur)));
    } catch {
      setSample((cur) => (cur ? { error: "The sample could not be rendered." } : cur));
    }
  };

  const cert = splitPosition(org.iso_position_certificate);
  const isoOnTop = !!org.iso_number && cert.vertical === "top";
  const band = bottomLine(
    [`Certificate No: ${numbering?.next_number || "—"}`, `Issued: ${fmtDate(isoDate(new Date()))}`],
    org.iso_number && cert.vertical === "bottom" ? org.iso_number : "", cert.align);

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <div className="overline text-primary">Configuration</div>
        <h2 className="font-heading font-bold text-3xl mt-1">Settings</h2>
      </div>

      {notLoaded.length > 0 && (
        <div role="alert" data-testid="settings-load-error"
             className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium text-destructive-text">Could not load the saved {notLoaded.join(", ")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              A section that did not load shows placeholders, not what is stored, and cannot be saved until it loads.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={retry} disabled={retrying} data-testid="settings-retry">
            {retrying ? "Loading..." : "Try again"}
          </Button>
        </div>
      )}

      <Card className="p-6 border border-border" data-testid="org-settings">
        <h3 className="font-heading font-semibold mb-1">ISO reference</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
          Printed on every certificate, register and attendance sheet, so an auditor reading any of
          them sees which standard the record is kept under.
        </p>
        <fieldset className="border-0 p-0 m-0 space-y-6" disabled={!writable || !loaded.org}>
          <div className="max-w-xl">
            <Label htmlFor="org-iso">ISO certification reference</Label>
            <Input id="org-iso" value={org.iso_number} onChange={(e) => setOrg({ ...org, iso_number: e.target.value })}
                   placeholder="e.g. ISO 9001:2015 · Certificate No. IN-12345" maxLength={120}
                   data-testid="org-iso" />
            <p className="text-xs text-muted-foreground mt-1">
              Write it exactly as it should print. Leave it blank and nothing is printed.
            </p>
          </div>
          <div>
            <div className="text-sm font-medium">Where it prints</div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Pick a place on each kind of page; whatever else is on that edge keeps its order around it.
              It is read each time a document is made, so it also applies to copies of certificates
              already issued.
            </p>
            <div className="mt-3 flex flex-wrap items-start gap-x-10 gap-y-5">
              <IsoPlacementPicker label="Certificates" shape="landscape" value={org.iso_position_certificate}
                                  onChange={(v) => setOrg({ ...org, iso_position_certificate: v })}
                                  disabled={!writable || !loaded.org} testid="iso-place-certificate" />
              <IsoPlacementPicker label="Registers and attendance sheets" shape="portrait" value={org.iso_position_documents}
                                  onChange={(v) => setOrg({ ...org, iso_position_documents: v })}
                                  disabled={!writable || !loaded.org} testid="iso-place-documents" />
            </div>
          </div>
        </fieldset>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {writable && (
            <Button onClick={saveOrg} disabled={savingOrg || !loaded.org || !orgDirty} data-testid="save-org">
              {savingOrg ? "Saving..." : "Save"}
            </Button>
          )}
          <Button variant="outline" onClick={openSample} disabled={!loaded.org} data-testid="preview-sample-org">
            <FileText className="w-4 h-4 mr-1.5" /> Preview a sample certificate
          </Button>
          {writable && orgDirty && <span className="text-xs text-muted-foreground" data-testid="org-unsaved">Unsaved changes</span>}
        </div>
      </Card>

      <Card className="p-6 border border-border" data-testid="documents-settings">
        <h3 className="font-heading font-semibold mb-1">Document control</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
          The header block on the annual calendar (F-TRG-03), the individual training record (F-TRG-04)
          and the attendance sheet (F-TRG-05): your document number, revision and effective date. Change
          them here when you revise a form — every copy printed afterwards carries the new revision.
        </p>
        {docs ? (
          <DocumentControl saved={docs} writable={writable} onSaved={setDocs} />
        ) : (
          <div className="text-sm text-muted-foreground" data-testid="documents-pending">
            {loaded.documents === false ? "Not loaded — use Try again above." : "Loading..."}
          </div>
        )}
      </Card>

      <Card className="p-6 border border-border" data-testid="numbering-settings">
        <h3 className="font-heading font-semibold mb-1">Certificate numbers</h3>
        <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
          Every certificate is given the next number automatically, and no number is ever given twice.
          Choose the format here. Numbers already issued never change — each is printed, and inside its
          QR code.
        </p>
        {numbering ? (
          <CertificateNumbering saved={numbering} writable={writable} onSaved={setNumbering} />
        ) : (
          <div className="text-sm text-muted-foreground" data-testid="numbering-pending">
            {loaded.numbering === false ? "Not loaded — use Try again above." : "Loading..."}
          </div>
        )}
      </Card>

      <Card className="p-6 border border-border">
        <h3 className="font-heading font-semibold mb-4">Certificate template</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <fieldset className="space-y-3 border-0 p-0 m-0" disabled={!writable || !loaded.templates}>
            {/* With more than one template there was no way to reach the others at all:
                the form loaded one and every other template was uneditable through the
                UI, permanently. */}
            {templates.length > 1 && (
              <div>
                <Label htmlFor="tpl-pick">Editing</Label>
                <Select value={t.id || ""} onValueChange={(v) => setT(templates.find((x) => x.id === v) || t)}>
                  <SelectTrigger id="tpl-pick" data-testid="tpl-pick"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        {x.name}{x.is_default ? " · default" : ""}{x.category ? ` · ${x.category}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">
                  A certificate is issued from the <b>default</b>, or from the one matching
                  its category. The wording is pinned to each certificate when it is
                  issued, so editing here changes what future ones say — not ones already
                  issued.
                </p>
              </div>
            )}
            <div><FieldLabel htmlFor="tpl-name" required>Template name</FieldLabel><Input id="tpl-name" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="tpl-name" /></div>
            <div><Label htmlFor="tpl-category">Category (blank = default)</Label><ComboBox id="tpl-category" testid="tpl-category" value={t.category || ""} onChange={(v) => setT({ ...t, category: v })} options={cats} placeholder="Any category" /></div>
            <div><FieldLabel htmlFor="tpl-body" required>Body text (supports placeholders)</FieldLabel><Textarea id="tpl-body" rows={4} value={t.body_text} onChange={(e) => setT({ ...t, body_text: e.target.value })} data-testid="tpl-body" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="tpl-sig1-name">Signatory 1 name</Label><Input id="tpl-sig1-name" value={t.signatory_1_name || ""} onChange={(e) => setT({ ...t, signatory_1_name: e.target.value })} /></div>
              <div><Label htmlFor="tpl-sig1-desig">Signatory 1 designation</Label><ComboBox id="tpl-sig1-desig" testid="tpl-sig1-desig" value={t.signatory_1_designation || ""} onChange={(v) => setT({ ...t, signatory_1_designation: v })} options={hints.designations || []} placeholder="e.g. Head - HR" /></div>
              <div><Label htmlFor="tpl-sig2-name">Signatory 2 name</Label><Input id="tpl-sig2-name" value={t.signatory_2_name || ""} onChange={(e) => setT({ ...t, signatory_2_name: e.target.value })} /></div>
              <div><Label htmlFor="tpl-sig2-desig">Signatory 2 designation</Label><ComboBox id="tpl-sig2-desig" testid="tpl-sig2-desig" value={t.signatory_2_designation || ""} onChange={(v) => setT({ ...t, signatory_2_designation: v })} options={hints.designations || []} placeholder="e.g. Plant Head" /></div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">
              Leave a signatory blank and the certificate prints no line for them.
            </p>
            <div className="flex items-center gap-3"><Switch checked={!!t.is_default} onCheckedChange={(v) => setT({ ...t, is_default: v })} aria-label="Set as default template" /> <span className="text-sm">Set as default template</span></div>
            {writable && <Button onClick={save} data-testid="save-template">Save template</Button>}
          </fieldset>
          <div className="border border-border rounded-md p-6 bg-muted/30 flex flex-col" data-testid="certificate-preview">
            {/* The ISO line sits where the certificate puts it: along the top, above or beside
                the mark, or on the bottom line with the number and the date. */}
            {isoOnTop && (
              <div className="grid grid-cols-3 gap-2 text-[10px] text-muted-foreground mb-2" data-testid="preview-top">
                {["left", "center", "right"].map((slot) => (
                  <div key={slot} className={cn(SLOT_TEXT[slot], "break-words line-clamp-2")}
                       data-testid={slot === cert.align ? "preview-iso" : undefined}>
                    {slot === cert.align ? org.iso_number : ""}
                  </div>
                ))}
              </div>
            )}
            <div className="flex-1">
              <div className="overline text-primary text-center">Preview</div>
              {/* The two marks where the certificate puts them — Shakun's left, the group's (Orbia)
                  right. A preview without them would be showing a document that does not exist. */}
              <div className="flex items-center justify-between gap-3 mt-3" data-testid="preview-marks">
                <span>
                  <img src="/brand/shakun-logo.svg" alt="" aria-hidden="true" className="h-7 w-auto dark:hidden" width="118" height="28" />
                  <img src="/brand/shakun-logo-light.svg" alt="" aria-hidden="true" className="h-7 w-auto hidden dark:block" width="118" height="28" />
                </span>
                <span>
                  <img src="/brand/orbia-logo.png" alt="" aria-hidden="true" className="h-6 w-auto dark:hidden" width="63" height="24" />
                  <img src="/brand/orbia-logo-light.png" alt="" aria-hidden="true" className="h-6 w-auto hidden dark:block" width="63" height="24" />
                </span>
              </div>
              <div className="text-center">
                <div className="font-heading text-xl font-bold mt-3">Certificate of Participation</div>
                <div className="text-sm text-muted-foreground mt-1">This is to certify that</div>
                <div className="font-heading text-2xl font-bold text-primary mt-3">[Employee Name]</div>
                <div className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{t.body_text}</div>
                {/* The title large and last, its facts as labelled fields beneath — HR's markup. */}
                <div className="font-heading text-lg font-bold text-primary mt-3">[Training title]</div>
                <div className="mt-3 inline-flex divide-x divide-border text-xs" data-testid="preview-details">
                  {["Date", "Duration", "Venue"].map((label) => (
                    <div key={label} className="px-3">
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                      <div className="mt-0.5">[{label.toLowerCase()}]</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* A line to sign on only where the template names somebody — blank ones print nothing. */}
              {[1, 2].some((n) => (t[`signatory_${n}_name`] || t[`signatory_${n}_designation`] || "").trim()) && (
                <div className="grid grid-cols-2 gap-4 mt-6 text-xs" data-testid="preview-signatories">
                  {[1, 2].map((n) => {
                    const name = t[`signatory_${n}_name`] || "";
                    const role = t[`signatory_${n}_designation`] || "";
                    return (name.trim() || role.trim()) ? (
                      <div key={n} className="border-t border-border pt-2">{name || "—"}<div className="text-muted-foreground">{role}</div></div>
                    ) : <div key={n} />;
                  })}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-6 pt-2 border-t border-dashed border-border text-[10px] text-muted-foreground items-end"
                 data-testid="preview-band">
              {["left", "center", "right"].map((slot) => (
                <div key={slot} className={cn(SLOT_TEXT[slot], "break-words line-clamp-2")}
                     data-testid={org.iso_number && cert.vertical === "bottom" && slot === cert.align ? "preview-iso" : undefined}>
                  {band[slot] || ""}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" size="sm" onClick={openSample} data-testid="preview-sample-template">
                <FileText className="w-4 h-4 mr-1.5" /> Open the real PDF
              </Button>
            </div>
          </div>
        </div>
        {/* Grouped, because a flat list hid the one thing worth knowing: there are two
            units, and "{unit}" is the person's. Written as "at {unit}" it read as where
            the training happened and printed the wrong plant on a certificate. */}
        <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">About the person:</span>{" "}
            <span className="font-mono">{"{employee_name} {employee_code} {designation} {department} {employee_unit}"}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">About the training:</span>{" "}
            <span className="font-mono">{"{training_title} {training_category} {trainer_name} {training_date} {duration_hours} {venue} {training_unit}"}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">About the certificate:</span>{" "}
            <span className="font-mono">{"{certificate_number} {issue_date} {iso_number}"}</span>
          </div>
          <div className="pt-1">
            <b>{"{employee_unit}"}</b> is where the person works. <b>{"{training_unit}"}</b> is
            where the training was held — they are not always the same.{" "}
            <span className="font-mono">{"{unit}"}</span> still works and means the person&apos;s unit.
          </div>
        </div>
      </Card>

      <Card className="p-6 border border-border">
        <h3 className="font-heading font-semibold mb-3">Training categories</h3>
        <div className="flex gap-2">
          <Input value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="Safety, Technical, ..." data-testid="cat-input" disabled={!writable || !loaded.categories} aria-label="Training categories, comma separated" />
          {writable && <Button onClick={saveCategories} disabled={!loaded.categories} data-testid="save-categories">Save</Button>}
        </div>
      </Card>

      <Dialog open={!!sample} onOpenChange={(open) => { if (!open) closeSample(); }}>
        <DialogContent className="max-w-5xl w-[calc(100vw-2rem)]" data-testid="sample-dialog">
          <DialogHeader>
            <DialogTitle>Sample certificate</DialogTitle>
            <DialogDescription>
              Made by the same code as a real certificate, from what is on this screen — nothing is saved.
              It is marked SAMPLE, and its number is the one the next real certificate will carry.
            </DialogDescription>
          </DialogHeader>
          {sample?.url ? (
            <iframe title="Sample certificate" src={`${sample.url}#view=Fit`} data-testid="sample-frame"
                    className="w-full aspect-[297/210] rounded-md border border-border bg-muted" />
          ) : sample?.error ? (
            <div className="aspect-[297/210] grid place-items-center rounded-md border border-border p-6 text-center" data-testid="sample-error">
              <div>
                <div className="text-sm text-destructive-text">{sample.error}</div>
                <Button variant="outline" size="sm" className="mt-3" onClick={openSample}>Try again</Button>
              </div>
            </div>
          ) : (
            <div className="aspect-[297/210] grid place-items-center rounded-md border border-border text-sm text-muted-foreground" data-testid="sample-loading">
              Rendering the sample...
            </div>
          )}
          {sample?.url && (
            <DialogFooter>
              <Button variant="outline" asChild>
                <a href={sample.url} download="certificate-sample.pdf">Download the sample</a>
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
